import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetchPrograms from '../../hooks/useFetchPrograms';
import './CoursesList.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DEFAULT_FILTERS = { query: '', type: 'all', status: 'all', unit: 'all' };

const ProgramsList = () => {
  const {
    diplomas,
    courses,
    errorDiplomas,
    errorCourses,
    loadingDiplomas,
    loadingCourses,
  } = useFetchPrograms();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState('az');
  const [showFilters, setShowFilters] = useState(false);

  // Utility function to get translated content
  const getTranslatedContent = (content, language) =>
    typeof content === 'object' && content !== null ? content[language] || content['en'] || '' : content;

  useEffect(() => {
    // Set text direction based on current language
    document.body.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.body.lang = i18n.language;

    // Handle Errors with Toast Notifications
    if (errorDiplomas) toast.error(t('failedToLoadDiplomas'));
    if (errorCourses) toast.error(t('failedToLoadCourses'));
  }, [i18n.language, errorDiplomas, errorCourses, t]);

  const formatDuration = (duration) =>
    duration?.amount && duration?.unit ? `${duration.amount} ${t(duration.unit.toLowerCase())}` : t('notSpecified');

  const handleEnrollClick = (isEnrollmentOpen, type, id) => {
    if (isEnrollmentOpen) {
      navigate(`/enroll/${type}/${id}`);
    } else {
      toast.info(t('enrollmentClosed'));
    }
  };

  // One normalised list of diplomas + courses
  const programs = useMemo(() => {
    const d = (diplomas || []).map((x) => ({
      id: x.id,
      type: 'diploma',
      title: getTranslatedContent(x.name, i18n.language),
      text: getTranslatedContent(x.description, i18n.language),
      duration: x.duration,
      open: !!x.enrollmentOpen,
    }));
    const c = (courses || []).map((x) => ({
      id: x.id,
      type: 'course',
      title: getTranslatedContent(x.title, i18n.language),
      text: getTranslatedContent(x.subtitle, i18n.language),
      duration: x.duration,
      open: !!x.enrollmentOpen,
    }));
    return [...d, ...c];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diplomas, courses, i18n.language]);

  const unitOf = (p) => (p.duration?.amount && p.duration?.unit ? p.duration.unit.toLowerCase() : 'unspecified');

  const units = useMemo(() => {
    const counts = {};
    programs.forEach((p) => {
      counts[unitOf(p)] = (counts[unitOf(p)] || 0) + 1;
    });
    return counts;
  }, [programs]);

  const visible = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    const list = programs.filter(
      (p) =>
        (filters.type === 'all' || p.type === filters.type) &&
        (filters.status === 'all' || (filters.status === 'open' ? p.open : !p.open)) &&
        (filters.unit === 'all' || unitOf(p) === filters.unit) &&
        (!q || (p.title || '').toLowerCase().includes(q) || (p.text || '').toLowerCase().includes(q))
    );
    const byTitle = (a, b) => (a.title || '').localeCompare(b.title || '', i18n.language);
    return [...list].sort((a, b) => {
      if (sort === 'za') return byTitle(b, a);
      if (sort === 'open') return Number(b.open) - Number(a.open) || byTitle(a, b);
      return byTitle(a, b);
    });
  }, [programs, filters, sort, i18n.language]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const countBy = (pred) => programs.filter(pred).length;
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  const loading = loadingDiplomas || loadingCourses;

  const unitLabel = (u) => (u === 'unspecified' ? t('notSpecified') : t(u));

  const Option = ({ name, value, label, count }) => (
    <label className={`pl-option ${filters[name] === value ? 'is-active' : ''}`}>
      <input
        type="radio"
        name={name}
        checked={filters[name] === value}
        onChange={() => setFilter(name, value)}
      />
      <span className="pl-option__dot"></span>
      <span className="pl-option__label">{label}</span>
      <span className="pl-option__count">{count}</span>
    </label>
  );

  const activeChips = [
    filters.type !== 'all' && { key: 'type', label: filters.type === 'diploma' ? t('diplomas') : t('courses') },
    filters.status !== 'all' && { key: 'status', label: filters.status === 'open' ? t('enrollmentOpen', 'Enrollment open') : t('enrollmentClosed') },
    filters.unit !== 'all' && { key: 'unit', label: unitLabel(filters.unit) },
    filters.query && { key: 'query', label: `“${filters.query}”` },
  ].filter(Boolean);

  return (
    <div className="pl-page">
      {/* Hero band */}
      <header className="pl-hero">
        <div className="container">
          <span className="pl-hero__eyebrow">{t('diplomas')} &amp; {t('courses')}</span>
          <h1 className="pl-main-title">{t('exploreOurPrograms')}</h1>
          <div className="pl-underline"></div>
          <ul className="pl-hero__stats">
            <li><strong>{countBy((p) => p.type === 'diploma')}</strong> {t('diplomas')}</li>
            <li><strong>{countBy((p) => p.type === 'course')}</strong> {t('courses')}</li>
            <li><strong>{countBy((p) => p.open)}</strong> {t('enrollmentOpen', 'Enrollment open')}</li>
          </ul>
        </div>
      </header>

      <div className="container pl-layout">
        {/* Filter sidebar */}
        <aside className={`pl-sidebar ${showFilters ? 'is-open' : ''}`} aria-label="Filters">
          <div className="pl-sidebar__head">
            <h2 className="pl-sidebar__title"><i className="fas fa-sliders-h"></i> {t('filters', 'Filters')}</h2>
            {isFiltered && (
              <button className="pl-clear" onClick={clearFilters}>{t('clearAll', 'Clear all')}</button>
            )}
          </div>

          <div className="pl-facet">
            <div className="pl-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                className="form-control"
                placeholder={t('searchPrograms', 'Search programs…')}
                aria-label={t('searchPrograms', 'Search programs…')}
                value={filters.query}
                onChange={(e) => setFilter('query', e.target.value)}
              />
            </div>
          </div>

          <fieldset className="pl-facet">
            <legend>{t('programType', 'Program type')}</legend>
            <Option name="type" value="all" label={t('all', 'All')} count={programs.length} />
            <Option name="type" value="diploma" label={t('diplomas')} count={countBy((p) => p.type === 'diploma')} />
            <Option name="type" value="course" label={t('courses')} count={countBy((p) => p.type === 'course')} />
          </fieldset>

          <fieldset className="pl-facet">
            <legend>{t('enrollment', 'Enrollment')}</legend>
            <Option name="status" value="all" label={t('all', 'All')} count={programs.length} />
            <Option name="status" value="open" label={t('enrollmentOpen', 'Enrollment open')} count={countBy((p) => p.open)} />
            <Option name="status" value="closed" label={t('enrollmentClosed')} count={countBy((p) => !p.open)} />
          </fieldset>

          <fieldset className="pl-facet">
            <legend>{t('duration')}</legend>
            <Option name="unit" value="all" label={t('all', 'All')} count={programs.length} />
            {Object.entries(units).map(([u, n]) => (
              <Option key={u} name="unit" value={u} label={unitLabel(u)} count={n} />
            ))}
          </fieldset>
        </aside>

        {/* Results */}
        <main className="pl-results">
          <div className="pl-toolbar">
            <button className="pl-filter-toggle" onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}>
              <i className="fas fa-sliders-h"></i> {t('filters', 'Filters')}
              {isFiltered && <span className="pl-filter-toggle__dot"></span>}
            </button>

            <p className="pl-count">
              <strong>{visible.length}</strong> {t('programsFound', 'programs')}
            </p>

            <label className="pl-sort">
              <span>{t('sortBy', 'Sort by')}</span>
              <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="az">{t('sortAz', 'Title A–Z')}</option>
                <option value="za">{t('sortZa', 'Title Z–A')}</option>
                <option value="open">{t('sortOpen', 'Open enrollment first')}</option>
              </select>
            </label>
          </div>

          {activeChips.length > 0 && (
            <div className="pl-chips">
              {activeChips.map((c) => (
                <button key={c.key} className="pl-chip" onClick={() => setFilter(c.key, DEFAULT_FILTERS[c.key])}>
                  {c.label} <i className="fas fa-times"></i>
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="pl-loading-container text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('loading')}</span>
              </div>
              <p>{t('loading')}</p>
            </div>
          ) : visible.length > 0 ? (
            <div className="pl-programs-grid">
              {visible.map((p) => (
                <article key={`${p.type}-${p.id}`} className="pl-program-card">
                  <div className={`pl-card-banner pl-card-banner--${p.type}`}>
                    <span className="pl-card-icon">
                      <i className={p.type === 'diploma' ? 'fas fa-graduation-cap' : 'fas fa-book-open'}></i>
                    </span>
                    <span className="pl-badge">{p.type === 'diploma' ? t('diploma', 'Diploma') : t('course', 'Course')}</span>
                    <span className={`pl-enrollment-status ${p.open ? 'open' : 'closed'}`}>
                      {p.open ? t('enrollmentOpen', 'Enrollment open') : t('enrollmentClosed')}
                    </span>
                  </div>

                  <div className="pl-card-body">
                    <h3 className="pl-card-title">{p.title || t('noTitle')}</h3>
                    <p className="pl-card-description">{p.text}</p>
                    <p className="pl-card-duration">
                      <i className="far fa-clock"></i> {formatDuration(p.duration)}
                    </p>
                    <div className="pl-button-group">
                      <button className="pl-link-btn" onClick={() => navigate(`/${p.type}-details/${p.id}`)}>
                        {t('viewDetails')} <i className="fas fa-arrow-right"></i>
                      </button>
                      <button
                        className={`btn btn-primary btn-sm pl-enroll-btn ${p.open ? '' : 'is-closed'}`}
                        onClick={() => handleEnrollClick(p.open, p.type, p.id)}
                        aria-disabled={!p.open}
                      >
                        {t('enroll')}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="pl-empty">
              <i className="far fa-folder-open"></i>
              <p>{t('noItemsAvailable')}</p>
              {isFiltered && <button className="btn btn-outline-primary" onClick={clearFilters}>{t('clearAll', 'Clear all')}</button>}
            </div>
          )}
        </main>
      </div>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default ProgramsList;
